import { ref, readonly, computed, watch, onMounted } from 'vue'
import type { LocationQuery, RouteLocationNormalizedLoaded } from 'vue-router'
import type { Router } from 'vue-router'
import { useVehiclesStore } from '~/stores/vehicles'

// Constants
const SYNC_DEBOUNCE_MS = 500
const URL_UPDATE_DEBOUNCE_MS = 800

// Declare global types for TypeScript
declare global {
  interface Window {
    __isSynchronizing?: boolean;
    __FILTERS_EXPLICITLY_CLEARED__?: boolean;
    __synchronizingFilters?: boolean;
    __lastVehiclesStoreUpdate?: number;
    __lastPageFiltersUpdate?: number;
    __synchronizationStartTime?: number;
    __lastSyncTime?: number;
    __lastUrlUpdateTime?: number;
    __PENDING_URL_RESTORE__?: boolean;
    __INITIAL_QUERY__?: Record<string, any>;
    __FILTER_STATE__?: Record<string, any>;
  }
}

// Define interface for vehicles store to avoid direct imports
interface VehiclesStore {
  activeFilters: Record<string, any>;
  getFilterByName: (name: string) => { data?: { min?: number; max?: number } } | undefined;
  updateFilterCounts: () => void;
  updateModelOptions: () => void;
}

// Add URL state tracking
const urlState = ref({
  lastQuery: {} as Record<string, any>,
  isInitialized: false,
  lastUpdateTime: 0,
  pendingUpdates: 0
})

// Add initialization tracking
const isInitialized = ref(false)

// Function to mark initialization as complete
const markInitialized = () => {
  isInitialized.value = true
}

/**
 * Composable for handling filter synchronization between store and URL
 * This is the single source of truth for filter synchronization
 */
export function useFilterSync() {
  // State
  const isInitializing = ref(true)
  const isSyncing = ref(false)
  const userHasInteracted = ref(false)
  const interactedFilters = ref(new Set<string>())
  const lastSyncTime = ref(0)
  const syncTimeoutId = ref<NodeJS.Timeout | null>(null)
  
  // Internal URL state management functions
  const updateURLState = (query: Record<string, any>) => {
    urlState.value.lastQuery = query
    urlState.value.lastUpdateTime = Date.now()
  }
  
  // Computed properties
  const hasInteractedWithAnyFilter = computed(() => userHasInteracted.value)
  
  // Constants
  const essentialFilters = [
    'make', 'model', 'badge', 'body', 'transmission', 
    'drivetrain', 'fuel', 'seats', 'doors', 'colour',
    'condition', 'search_keywords', 'favorites'
  ]
  
  const rangeFilters = ['price', 'kms', 'perweek', 'year']
  
  /**
   * Track user interaction with a filter
   */
  function trackFilterInteraction(filterName: string) {
    interactedFilters.value.add(filterName)
    userHasInteracted.value = true
  }
  
  /**
   * Check if user has interacted with a specific filter
   */
  function hasInteractedWith(filterName: string): boolean {
    return interactedFilters.value.has(filterName)
  }
  
  /**
   * Clear interaction tracking for a specific filter
   */
  function clearInteraction(filterName: string) {
    interactedFilters.value.delete(filterName)
    
    // If no filters remain, reset user interaction flag
    if (interactedFilters.value.size === 0) {
      userHasInteracted.value = false
    }
  }
  
  /**
   * Clear all interaction tracking
   */
  function clearAllInteractions() {
    interactedFilters.value.clear()
    userHasInteracted.value = false
  }
  
  /**
   * Generate a query object from the current store state
   */
  function generateQueryFromStore(vehiclesStore: VehiclesStore): Record<string, string | string[]> {
    const query: Record<string, string | string[]> = {}
    const activeFilters = vehiclesStore.activeFilters
    
    // Process each active filter
    Object.entries(activeFilters).forEach(([filterName, filterValue]) => {
      // Skip undefined or null values
      if (filterValue === undefined || filterValue === null) return
      
      // Skip internal properties
      if (filterName.startsWith('_')) return
      
      // Handle array filters (make, model, badge, etc.)
      if (Array.isArray(filterValue) && filterValue.length > 0) {
        query[filterName] = filterValue.join(',')
      }
      // Handle object with values array
      else if (typeof filterValue === 'object' && filterValue !== null && 'type' in filterValue) {
        const values = (filterValue as any).values
        if (Array.isArray(values) && values.length > 0) {
          // Special handling for transmission filter with hyphenated values
          if (filterName === 'transmission') {
            // Preserve hyphenated values as is
            query[filterName] = values.join(',')
          } else {
            // Normal comma-separated values
            query[filterName] = values.join(',')
          }
        }
      }
      // Handle range filters (price, kms, perweek)
      else if (typeof filterValue === 'object' && filterValue !== null && 
               'min' in filterValue && 'max' in filterValue && 'type' in filterValue) {
        // Get default values from filter definition
        const filter = vehiclesStore.getFilterByName(filterName)
        if (filter?.data) {
          const defaultMin = filter.data.min ?? 0
          const defaultMax = filter.data.max ?? 10000000
          
          const typedFilterValue = filterValue as { min: number; max: number }
          
          // Debug logging for perweek filter
          if (filterName === 'perweek') {
            // console.log(`[FilterSync] Processing perweek range filter: min=${typedFilterValue.min}, max=${typedFilterValue.max}, default min=${defaultMin}, default max=${defaultMax}`)
          }
          
          // Only add to query if different from default
          if (typedFilterValue.min !== defaultMin || typedFilterValue.max !== defaultMax) {
            // Always use hyphen format for range filters, never separate min/max params
            query[filterName] = `${typedFilterValue.min}-${typedFilterValue.max}`
            
            // Debug logging for perweek filter
            if (filterName === 'perweek') {
              // console.log(`[FilterSync] Added perweek to URL query: ${query[filterName]}`)
            }
          }
        }
      }
      // Handle boolean filters (favorites)
      else if (typeof filterValue === 'boolean') {
        if (filterValue) {
          query[filterName] = 'true'
        }
      }
      // Handle text filters (search_keywords)
      else if (typeof filterValue === 'object' && filterValue !== null && 
               'type' in filterValue && (filterValue as any).type === 'text' && 
               'value' in filterValue) {
        const searchValue = (filterValue as any).value.trim()
        if (searchValue) {
          query[filterName] = searchValue
        }
      }
    })
    
    return query
  }
  
  /**
   * Compare two query objects for equivalence
   */
  function areQueriesEquivalent(query1: Record<string, any>, query2: Record<string, any>): boolean {
    const keys1 = Object.keys(query1)
    const keys2 = Object.keys(query2)
    
    // If key counts differ, queries are not equivalent
    if (keys1.length !== keys2.length) {
      return false
    }
    
    // Check each key
    for (const key of keys1) {
      if (!(key in query2)) {
        return false
      }
      
      const val1 = query1[key]
      const val2 = query2[key]
      
      // Normalize values for comparison
      const normalize = (val: any, keyName?: string): string => {
        if (val === null || val === undefined) return ''
        
        // Special handling for transmission with hyphenated values
        if (keyName === 'transmission' && typeof val === 'string' && val.includes('-') && !val.includes(',')) {
          return val.trim()
        }
        
        if (Array.isArray(val)) {
          return val.map(v => String(v).trim()).filter(Boolean).sort().join(',')
        }
        
        if (typeof val === 'string') {
          return val.split(',').map(v => v.trim()).filter(Boolean).sort().join(',')
        }
        
        return String(val).trim()
      }
      
      const normalizedVal1 = normalize(val1, key)
      const normalizedVal2 = normalize(val2, key)
      
      if (normalizedVal1 !== normalizedVal2) {
        return false
      }
    }
    
    return true
  }
  
  /**
   * Add a check for filter changes before updating URL
   */
  function shouldUpdateURL(query: Record<string, any>, currentQuery: Record<string, any>): boolean {
    // Skip if queries are equivalent
    if (areQueriesEquivalent(query, currentQuery)) {
      return false
    }
    
    // Skip if we're in the middle of a sync operation
    if (isSyncing.value) {
      return false
    }
    
    // Skip if we're initializing
    if (isInitializing.value) {
      return false
    }
    
    return true
  }
  
  /**
   * Update URL with filter parameters
   */
  function updateURL(
    route: RouteLocationNormalizedLoaded,
    router: Router,
    vehiclesStore: VehiclesStore
  ) {
    if (isSyncing.value) {
      return
    }
    
    try {
      isSyncing.value = true
      
      // Set flag to prevent router plugin from interferring
      if (process.client && window) {
        window.__isSynchronizing = true
      }
      
      // Create a mutable copy of the route query
      const query = { ...route.query }
      
      // Generate new query from store
      const newQuery = generateQueryFromStore(vehiclesStore)
      
      // Check if we should update the URL
      if (shouldUpdateURL(newQuery, query)) {
        // Ensure all query values are strings, not objects
        const cleanQuery: Record<string, string> = {}
        for (const [key, value] of Object.entries(newQuery)) {
          // Skip null or undefined values
          if (value === null || value === undefined) continue
          
          // Handle different value types
          if (typeof value === 'string') {
            cleanQuery[key] = value
          } else if (typeof value === 'number' || typeof value === 'boolean') {
            cleanQuery[key] = String(value)
          } else if (Array.isArray(value)) {
            cleanQuery[key] = value.join(',')
          } else {
            // Skip objects or other complex types
          }
        }
        
        router.replace({ query: cleanQuery }).catch(error => {
        })
      }
    } catch (error) {
    } finally {
      // Reset sync flag after a short delay to allow for navigation
      setTimeout(() => {
        isSyncing.value = false
        
        if (process.client && window) {
          window.__isSynchronizing = false
        }
      }, 100)
    }
  }
  
  /**
   * Apply filters from URL query parameters
   */
  function applyFiltersFromURL(
    query: LocationQuery,
    vehiclesStore: VehiclesStore
  ): boolean {
    if (isSyncing.value) {
      return false
    }
    
    try {
      isSyncing.value = true
      
      // Set global flag to prevent router from restoring parameters
      if (process.client && window) {
        window.__isSynchronizing = true
      }
      
      let filtersApplied = false
      const updates: Record<string, any> = {}
      
      // Process essential filters (non-range filters)
      essentialFilters.forEach(filterName => {
        const value = query[filterName]
        
        if (value !== undefined && value !== null) {
          // Handle search_keywords
          if (filterName === 'search_keywords') {
            const searchString = String(Array.isArray(value) ? value[0] : value).trim()
            updates[filterName] = { type: 'text', value: searchString }
            filtersApplied = true
          }
          // Handle favorites
          else if (filterName === 'favorites') {
            updates[filterName] = (Array.isArray(value) ? value[0] : value) === 'true'
            filtersApplied = true
          }
          // Handle array filters
          else {
            let urlValues: string[]
            
            if (typeof value === 'string') {
              // Special handling for transmission filter with hyphenated values
              if (filterName === 'transmission') {
                // Split by comma but preserve hyphenated values
                urlValues = value.split(',').map(v => v.trim()).filter(Boolean)
              } else {
                // Normal comma-separated values
                urlValues = value.split(',').map(v => v.trim()).filter(Boolean)
              }
            } else if (Array.isArray(value)) {
              urlValues = value.filter(v => v !== null && v !== undefined).map(String)
            } else {
              urlValues = []
            }
            
            if (urlValues.length > 0) {
              updates[filterName] = { type: 'array', values: urlValues }
              filtersApplied = true
            }
          }
        }
      })
      
      // Process range filters
      rangeFilters.forEach(filterName => {
        // First check for hyphen-separated format (PREFERRED FORMAT)
        const hyphenatedValue = query[filterName]
        
        // Debug log the presence of perweek and its format
        if (filterName === 'perweek') {
          // console.log(`[FilterSync] Processing perweek from URL. Value:`, hyphenatedValue)
          // Also log if perweek_min or perweek_max exist
          if (query['perweek_min'] || query['perweek_max']) {
            // console.log(`[FilterSync] Also found perweek_min=${query['perweek_min']} or perweek_max=${query['perweek_max']}`)
          }
        }
        
        if (typeof hyphenatedValue === 'string' && hyphenatedValue.includes('-')) {
          const [minStr, maxStr] = hyphenatedValue.split('-')
          const min = Number(minStr)
          const max = Number(maxStr)
          
          // Debug for perweek
          if (filterName === 'perweek') {
            // console.log(`[FilterSync] Found hyphenated perweek value: ${hyphenatedValue}, min=${min}, max=${max}`)
          }
          
          if (!isNaN(min) && !isNaN(max)) {
            const filter = vehiclesStore.getFilterByName(filterName)
            if (filter?.data) {
              updates[filterName] = { type: 'range', min, max }
              filtersApplied = true
              
              // Debug for perweek
              if (filterName === 'perweek') {
                // console.log(`[FilterSync] Applied perweek from hyphenated format: min=${min}, max=${max}`)
              }
              
              return // Skip the min/max parameter check if hyphen format was processed
            }
          }
        } else if (Array.isArray(hyphenatedValue) && hyphenatedValue.length > 0 && 
                   typeof hyphenatedValue[0] === 'string' && hyphenatedValue[0].includes('-')) {
          // Handle array of hyphenated values (take the first one)
          const [minStr, maxStr] = hyphenatedValue[0].split('-')
          const min = Number(minStr)
          const max = Number(maxStr)
          
          if (!isNaN(min) && !isNaN(max)) {
            const filter = vehiclesStore.getFilterByName(filterName)
            if (filter?.data) {
              updates[filterName] = { type: 'range', min, max }
              filtersApplied = true
              return // Skip the min/max parameter check if hyphen format was processed
            }
          }
        }
        
        // Then check for min/max parameter format ONLY if hyphen format wasn't found
        const minValueStr = query[`${filterName}_min`]
        const maxValueStr = query[`${filterName}_max`]
        
        if (minValueStr !== undefined || maxValueStr !== undefined) {
          const filter = vehiclesStore.getFilterByName(filterName)
          if (filter?.data) {
            // Get default values based on filter type
            let defaultMin = 0
            let defaultMax = 10000000
            
            if (filterName === 'year') {
              const currentYear = new Date().getFullYear()
              defaultMin = currentYear - 20
              defaultMax = currentYear
            } else {
              defaultMin = filter.data.min ?? 0
              defaultMax = filter.data.max ?? 10000000
            }
            
            // Parse values
            const urlMinStr = Array.isArray(minValueStr) ? minValueStr[0] : minValueStr
            const urlMaxStr = Array.isArray(maxValueStr) ? maxValueStr[0] : maxValueStr
            
            let newMin = (urlMinStr !== undefined && urlMinStr !== null) ? Number(urlMinStr) : defaultMin
            let newMax = (urlMaxStr !== undefined && urlMaxStr !== null) ? Number(urlMaxStr) : defaultMax
            
            // Skip invalid values
            if (isNaN(newMin) || isNaN(newMax)) {
              return
            }
            
            // Ensure values are within valid ranges
            if (filterName === 'year') {
              const currentYear = new Date().getFullYear()
              if (newMin < currentYear - 20) newMin = currentYear - 20
              if (newMax > currentYear) newMax = currentYear
            }
            
            updates[filterName] = { type: 'range', min: newMin, max: newMax }
            filtersApplied = true
          }
        }
      })
      
      // Apply all updates to the store at once
      if (filtersApplied) {
        Object.assign(vehiclesStore.activeFilters, updates)
        
        // Trigger necessary updates
        vehiclesStore.updateFilterCounts()
        vehiclesStore.updateModelOptions()
      }
      
      return filtersApplied
    } catch (error) {
      return false
    } finally {
      isSyncing.value = false
      
      // Reset global flag after a short delay
      if (process.client && window) {
        setTimeout(() => {
          window.__isSynchronizing = false
        }, 100)
      }
    }
  }
  
  /**
   * Clear URL parameters for filters
   */
  function clearURLParameters(route: RouteLocationNormalizedLoaded, router: Router) {
    try {
      isSyncing.value = true
      
      // Mark filters as explicitly cleared to prevent auto-restore
      if (process.client && window) {
        window.__FILTERS_EXPLICITLY_CLEARED__ = true;
      }
      
      // Get all filter-related keys, including both formats
      const filterKeys = [
        ...essentialFilters,
        ...rangeFilters,
        ...rangeFilters.flatMap(f => [`${f}_min`, `${f}_max`])
      ]
      
      // Create new query without filter parameters
      const newQuery = { ...route.query }
      let changed = false
      
      filterKeys.forEach(key => {
        if (key in newQuery) {
          delete newQuery[key]
          changed = true
        }
      })
      
      // Update URL if changed
      if (changed) {
        router.replace({ query: newQuery })
      }
    } catch (error) {
    } finally {
      isSyncing.value = false
    }
  }
  
  /**
   * Initialize the filter sync system
   * This should be called from a component's setup function or onMounted hook
   */
  function initialize(
    route: RouteLocationNormalizedLoaded,
    router: Router,
    vehiclesStore: VehiclesStore
  ) {
    // Set up watchers
    setupWatchers(route, router, vehiclesStore)
    
    // Apply initial filters from URL
    const applied = applyFiltersFromURL(route.query, vehiclesStore)
    
    // If filters were applied from URL, mark as user interaction
    if (applied) {
      userHasInteracted.value = true
      // Also mark all filters that were applied as interacted with
      Object.keys(route.query).forEach(key => {
        if (essentialFilters.includes(key) || rangeFilters.includes(key) || 
            rangeFilters.some(f => key === `${f}_min` || key === `${f}_max`)) {
          interactedFilters.value.add(key)
        }
      })
    }
    
    // Mark initialization as complete
    isInitializing.value = false
  }
  
  /**
   * Set up watchers for route and store changes
   */
  function setupWatchers(
    route: RouteLocationNormalizedLoaded,
    router: Router,
    vehiclesStore: VehiclesStore
  ) {
    // Watch for store changes to update URL
    watch(() => vehiclesStore.activeFilters, () => {
      if (isSyncing.value) {
        return
      }
      
      if (userHasInteracted.value) {
        // Debounce updates
        if (syncTimeoutId.value) {
          clearTimeout(syncTimeoutId.value)
        }
        
        syncTimeoutId.value = setTimeout(() => {
          updateURL(route, router, vehiclesStore)
        }, SYNC_DEBOUNCE_MS)
      }
    }, { deep: true })
    
    // Watch for URL changes to update store
    watch(() => route.query, (newQuery) => {
      if (isSyncing.value) {
        return
      }
      
      // Debounce updates
      if (syncTimeoutId.value) {
        clearTimeout(syncTimeoutId.value)
      }
      
      syncTimeoutId.value = setTimeout(() => {
        const applied = applyFiltersFromURL(newQuery, vehiclesStore)
        
        // If filters were applied from URL, mark as user interaction
        if (applied) {
          userHasInteracted.value = true
        }
      }, SYNC_DEBOUNCE_MS)
    }, { deep: true })
  }
  
  // Return public API
  return {
    // State
    isInitializing: readonly(isInitializing),
    isSyncing: readonly(isSyncing),
    userHasInteracted: readonly(userHasInteracted),
    hasInteractedWithAnyFilter,
    
    // Methods
    trackFilterInteraction,
    hasInteractedWith,
    clearInteraction,
    clearAllInteractions,
    updateURL,
    applyFiltersFromURL,
    clearURLParameters,
    initialize,
    
    // Constants
    essentialFilters,
    rangeFilters,
    markInitialized
  }
}

// Add URL synchronization function
export function synchronizeFilterParams(synchronizeURL = true) {
  if (synchronizeURL && process.client) {
    try {
      // Get router and route safely
      let route, router, vehiclesStore, filterSync;
      
      try {
        // Use Nuxt composables instead of window access
        if (typeof window !== 'undefined') {
          try {
            router = useRouter();
            route = useRoute();
          } catch (e) {
            console.warn('Router composables not available');
          }
        }
        
        // Get vehicles store using proper Pinia composable
        if (typeof window !== 'undefined') {
          try {
            vehiclesStore = useVehiclesStore();
          } catch (e) {
            console.warn('Vehicles store not available');
          }
        }
        
        // Try to get filter sync
        filterSync = useFilterSync();
      } catch (err) {
        // console.error('[FilterSync] Error accessing Nuxt composables:', err);
        return;
      }
      
      // Skip if we're in the middle of a filter sync operation
      if (window.__isSynchronizing) {
        return;
      }
      
      // Skip if route or router is not available
      if (!route || !router) {
        // console.log('[FilterSync] Route or router not available, skipping URL update');
        return;
      }
      
      // Skip if vehicles store is not available
      if (!vehiclesStore) {
        return;
      }
      
      // Update URL with current filter state
      filterSync.updateURL(route, router, vehiclesStore as any);
      
      // Reset synchronization flag after a delay
      setTimeout(() => {
        window.__isSynchronizing = false;
      }, 100);
    } catch (error) {
      // console.error('[FilterSync] Error synchronizing filter params:', error);
      if (process.client && window) {
        window.__isSynchronizing = false;
      }
    }
  }
}

// Add initialization function
export function initializeFilterSync() {
  if (process.client) {
    try {
      // Get router and route safely
      let route, router, vehiclesStore, filterSync;
      
      try {
        // Use Nuxt composables instead of window access
        if (typeof window !== 'undefined') {
          try {
            router = useRouter();
            route = useRoute();
          } catch (e) {
            console.warn('Router composables not available');
          }
        }
        
        // Get vehicles store using proper Pinia composable
        if (typeof window !== 'undefined') {
          try {
            vehiclesStore = useVehiclesStore();
          } catch (e) {
            console.warn('Vehicles store not available');
          }
        }
        
        // Try to get filter sync
        filterSync = useFilterSync();
      } catch (err) {
        // console.error('[FilterSync] Error accessing Nuxt composables:', err);
        return;
      }
      
      // Skip if route or router is not available
      if (!route || !router) {
        // console.log('[FilterSync] Route or router not available, skipping initialization');
        return;
      }
      
      // Skip if vehicles store is not available
      if (!vehiclesStore) {
        // console.log('[FilterSync] Vehicles store not available, skipping initialization');
        return;
      }
      
      // Store initial query parameters
      if (route.path === '/vehicles') {
        window.__INITIAL_QUERY__ = { ...route.query };
        
        // Apply filters from URL if they exist
        if (Object.keys(route.query).length > 0 && filterSync.applyFiltersFromURL) {
          // console.log('[FilterSync] Applying filters from URL query:', JSON.stringify(route.query));
          filterSync.applyFiltersFromURL(route.query, vehiclesStore as any);
        }
      }
      
      // Mark as initialized
      markInitialized();
    } catch (error) {
      // console.error('[FilterSync] Error initializing filter sync:', error);
    }
  }
}

// Add URL state restoration function
export function restoreUrlState() {
  if (process.client && window.__INITIAL_QUERY__) {
    try {
      // Get router and route safely
      let route, router;
      
      try {
        // Use Nuxt composables instead of window access
        if (typeof window !== 'undefined') {
          try {
            router = useRouter();
            route = useRoute();
          } catch (e) {
            console.warn('Router composables not available');
          }
        }
      } catch (err) {
        // console.error('[FilterSync] Error accessing Nuxt composables:', err);
        return;
      }
      
      // Skip if route or router is not available
      if (!route || !router) {
        // console.log('[FilterSync] Route or router not available, skipping URL restoration');
        return;
      }
      
      // Only restore if we're on the vehicles page and have initial query
      if (route.path === '/vehicles' && window.__INITIAL_QUERY__) {
        // console.log('[FilterSync] Restoring URL state:', JSON.stringify(window.__INITIAL_QUERY__));
        router.replace({ query: window.__INITIAL_QUERY__ })
          .catch((err: Error) => console.error('[FilterSync] Error replacing URL:', err)); // Keep error log
        
        // Clear initial query after restoration
        window.__INITIAL_QUERY__ = undefined;
      }
    } catch (error) {
      // console.error('[FilterSync] Error restoring URL state:', error);
    }
  }
}

// DO NOT create a singleton instance here, as it will cause Pinia errors
// The singleton will be created in the plugin instead
